import { useCallback, useEffect, useRef, useState } from 'react';

import clsx from 'clsx';

import type { HTMLAttributes } from 'react';

import './styles.scss';

export interface IAPScrollContainerProps extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
  step?: number;
  orientation?: 'horizontal' | 'vertical';
  'data-testid'?: string;
}

export const APScrollContainer = ({
  children,
  className = '',
  step,
  orientation = 'horizontal',
  'data-testid': testId,
  ...rest
}: IAPScrollContainerProps) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  const isHorizontal = orientation === 'horizontal';

  const updateScrollState = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;

    if (isHorizontal) {
      setCanPrev(el.scrollLeft > 0);
      setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
      setHasOverflow(el.scrollWidth > el.clientWidth + 1);
    } else {
      setCanPrev(el.scrollTop > 0);
      setCanNext(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
      setHasOverflow(el.scrollHeight > el.clientHeight + 1);
    }
  }, [isHorizontal]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    updateScrollState();

    const handleScroll = () => updateScrollState();
    el.addEventListener('scroll', handleScroll, { passive: true });

    const resizeObserver = new ResizeObserver(() => updateScrollState());
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [updateScrollState]);

  const scroll = useCallback(
    (direction: 'prev' | 'next') => {
      const el = viewportRef.current;
      if (!el) return;
      const dynamicStep =
        step ?? (isHorizontal ? el.clientWidth * 0.6 || 160 : el.clientHeight * 0.6 || 160);
      const delta = direction === 'next' ? dynamicStep : -dynamicStep;
      const options = isHorizontal
        ? { left: delta, behavior: 'smooth' as const }
        : { top: delta, behavior: 'smooth' as const };
      el.scrollBy(options);
    },
    [step, isHorizontal]
  );

  return (
    <div
      className={clsx(
        'ap-scroll',
        `ap-scroll--${orientation}`,
        !hasOverflow && 'ap-scroll--no-controls',
        className
      )}
      data-testid={testId}
      {...rest}
    >
      {hasOverflow && (
        <button
          type="button"
          className="ap-scroll__control ap-scroll__control--prev"
          onClick={() => scroll('prev')}
          disabled={!canPrev}
          aria-label="Scroll previous"
        >
          ‹
        </button>
      )}

      <div className="ap-scroll__viewport" ref={viewportRef}>
        <div className="ap-scroll__content">{children}</div>
      </div>

      {hasOverflow && (
        <button
          type="button"
          className="ap-scroll__control ap-scroll__control--next"
          onClick={() => scroll('next')}
          disabled={!canNext}
          aria-label="Scroll next"
        >
          ›
        </button>
      )}
    </div>
  );
};

export default APScrollContainer;
